import { Flex } from "antd"
import React, { useEffect } from "react"

import { FixedContainer } from "../../components/FixedContainer.tsx"
import { homeColor, homeSize } from "./home-theme.ts"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { BranchSetupFlow } from "../Branches/components/BranchSetupFlow.tsx"
import { CompanyDashboardPage } from "../Branches/pages/CompanyDashboardPage.tsx"
import { selectActiveBranchId, selectIsCompanyMode } from "../Branches/redux/branches.slice.ts"
import { HomeFavoriteReports } from "../ReportsPage/components/home/HomeFavoriteReports.tsx"
import { HomeBelowSections, HomeGreeting } from "./components/HomeSections.tsx"
import { MobileHome } from "./components/MobileHome"
import { WidgetsDashboard } from "./components/Portfolio/WidgetsDashboard.tsx"

// A single operational branch's own scope. `WidgetsDashboard` treats an
// empty/undefined `teamScope` as "every branch the caller can see" (the
// Company HQ no-filter case) — so a branch workspace MUST pass its own id
// here, or every tile silently falls back to the whole company's data,
// identically, whichever branch is selected.
const useHomeTeamScope = () => {
  const activeBranchId = useAppSelector(selectActiveBranchId)
  return activeBranchId ? [activeBranchId] : []
}

/**
 * Home's own canvas: the mockup's #F3F8FA ground and 24px gutters, bled out
 * over `#main-content`'s own padding so the colour reaches the shell's edges.
 * Scoped here, so no other page's background moves.
 */
const HomeCanvas = ({ children }: React.PropsWithChildren) => (
  <div
    className={
      "home-canvas -mx-2 lg:-mx-4 -mt-(--main-content-pad-y) px-4 lg:px-6 pt-5 pb-6 min-h-full"
    }
    style={{ background: homeColor.canvas }}
  >
    {/* Four metric columns is the desktop maximum, so past this width the
        content centres rather than stretching every card and table column. */}
    <Flex
      vertical
      gap={homeSize.sectionGap}
      className={"w-full mx-auto overflow-x-clip"}
      style={{ maxWidth: homeSize.maxContentWidth }}
    >
      {children}
    </Flex>
  </div>
)

const WidgetsHome = () => {
  const teamScope = useHomeTeamScope()

  useEffect(() => {
    generateDocumentTitle("Home")
  }, [])

  return (
    <FixedContainer>
      {/* `hidden` would make this a second scroller, stranding the column's bottom reserve as a blank band */}
      <HomeCanvas>
        <BranchSetupFlow />
        <WidgetsDashboard
          teamScope={teamScope}
          renderHeader={(viewControls, exportControl) => (
            <HomeGreeting
              viewControls={viewControls}
              exportControl={exportControl}
            />
          )}
        />
        <HomeFavoriteReports />
        <HomeBelowSections />
      </HomeCanvas>
    </FixedContainer>
  )
}

const MobileWidgetsHome = () => {
  useEffect(() => {
    generateDocumentTitle("Home")
  }, [])

  return (
    <FixedContainer>
      <div className={"overflow-x-clip"}>
        <BranchSetupFlow />
        <MobileHome />
      </div>
    </FixedContainer>
  )
}

const HomePage = () => {
  const isMobile = useAppSelector((state) => state.nav.isMobile)
  const isCompanyMode = useAppSelector(selectIsCompanyMode)

  // The company overview replaces this page's content entirely while a
  // multi-branch owner is looking at the whole company — the single-branch
  // operational Home (with its project-creation/search/filter toolbar)
  // stays exactly as it was for every other case, mobile included.
  if (isCompanyMode) return <CompanyDashboardPage />

  return isMobile ? <MobileWidgetsHome /> : <WidgetsHome />
}

export default HomePage
